import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListTodo, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';

/**
 * TodoOverview — the Site Diary dashboard (ProjectPicker) overview of every
 * OUTSTANDING to-do across the projects the user is assigned to, grouped by
 * project.
 *
 *   • Ticking an item marks it done. It stays visible (ticked) for the
 *     current session, then drops off the overview on the next app load —
 *     the overview only lists outstanding (not-yet-done) items. The done
 *     item carries over to that project's diary page (shown ticked, until
 *     archived there).
 *   • "Add" opens a popup with a project selector; adding closes the popup.
 */
export const TodoOverview = ({ projects = [] }) => {
  const { api } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await api.get('/diary/todos/overview');
      setTodos(res.data || []);
    } catch (_e) { /* non-blocking */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of todos) {
      if (!map.has(t.project_id)) {
        map.set(t.project_id, { project_id: t.project_id, project_ref: t.project_ref, items: [] });
      }
      map.get(t.project_id).items.push(t);
    }
    return Array.from(map.values());
  }, [todos]);

  const toggleDone = async (t) => {
    const next = !t.done;
    // Keep it visible (ticked) this session — it clears on the next load.
    setTodos(prev => prev.map(x => x.id === t.id ? { ...x, done: next } : x));
    try { await api.patch(`/diary/todos/${t.id}`, { done: next }); }
    catch (_e) { toast.error('Failed to update to-do'); fetchOverview(); }
  };

  const openCount = todos.filter(t => !t.done).length;

  return (
    <div
      className="mb-5 rounded-xl px-3.5 py-3"
      style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }}
      data-testid="diary-todo-overview"
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: '#EDE9FE', color: '#6D28D9' }}
        >
          <ListTodo className="w-4 h-4" strokeWidth={2.25} />
        </div>
        <div className="text-[11px] uppercase tracking-wider font-semibold flex-1" style={{ color: PAPER.muted }}>
          To-dos {openCount > 0 && <span style={{ color: PAPER.hint }}>· {openCount}</span>}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[12px] font-medium flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors hover:bg-[#FAF7F2]"
          style={{ color: PAPER.accent }}
          data-testid="diary-todo-overview-add-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>

      {loading ? (
        <div className="text-[12px] py-1.5" style={{ color: PAPER.muted }}>Loading…</div>
      ) : todos.length === 0 ? (
        <div className="text-[12px] py-1" style={{ color: PAPER.muted }}>Nothing outstanding. Tap Add to create a to-do.</div>
      ) : (
        <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="diary-todo-overview-list">
          {grouped.map(g => (
            <div key={g.project_id}>
              <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 truncate" style={{ color: PAPER.hint }}>
                {g.project_ref || 'Project'}
              </div>
              <div className="space-y-1.5">
                {g.items.map(t => (
                  <label
                    key={t.id}
                    className="flex items-start gap-2 cursor-pointer"
                    data-testid={`diary-todo-overview-item-${t.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!t.done}
                      className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#6D28D9]"
                      onChange={() => toggleDone(t)}
                      data-testid={`diary-todo-overview-check-${t.id}`}
                    />
                    <span
                      className={`text-[13px] leading-snug ${t.done ? 'line-through' : ''}`}
                      style={{ color: t.done ? PAPER.muted : PAPER.ink }}
                    >
                      {t.text}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <OverviewAddDialog
          projects={projects}
          onClose={() => { setOpen(false); fetchOverview(); }}
        />
      )}
    </div>
  );
};

/**
 * OverviewAddDialog — add a to-do from the dashboard. Requires a project
 * selector (to-dos are project-scoped). Adding closes the popup.
 */
const OverviewAddDialog = ({ projects = [], onClose }) => {
  const { api } = useAuth();
  const [projectId, setProjectId] = useState(projects[0]?.id || '');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const addTodo = async () => {
    if (!projectId) { toast.error('Choose a project'); return; }
    const clean = text.trim();
    if (!clean) return;
    setSaving(true);
    try {
      await api.post('/diary/todos', { project_id: projectId, text: clean });
      toast.success('To-do added');
      onClose(); // per spec: adding a to-do closes the popup
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add to-do');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(31,41,55,0.55)', backdropFilter: 'blur(4px)' }}
      data-testid="diary-todo-overview-dialog"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full rounded-2xl overflow-hidden"
        style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${PAPER.border}` }}>
          <ListTodo className="w-4 h-4" style={{ color: '#6D28D9' }} />
          <div className="text-[15px] font-bold flex-1" style={{ color: PAPER.ink }}>Add a to-do</div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[#FAF7F2]"
            style={{ color: PAPER.muted }}
            data-testid="diary-todo-overview-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>Project</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full mt-1 text-[13px] rounded-lg px-3 py-2 focus:outline-none focus:border-[#6D28D9]"
              style={{ background: PAPER.page, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
              data-testid="diary-todo-overview-project-select"
            >
              {projects.length === 0 && <option value="">No projects</option>}
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.project_ref}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>To-do</label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addTodo(); }}
              placeholder="What needs doing?"
              className="w-full mt-1 text-[13px] rounded-lg px-3 py-2 focus:outline-none focus:border-[#6D28D9]"
              style={{ background: PAPER.page, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
              data-testid="diary-todo-overview-new-input"
              autoFocus
            />
          </div>
        </div>

        <div className="px-4 py-3 flex gap-2 justify-end" style={{ background: PAPER.page, borderTop: `1px solid ${PAPER.border}` }}>
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] font-medium rounded-lg px-4 py-2 hover:bg-[#FAF7F2]"
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
            data-testid="diary-todo-overview-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={addTodo}
            disabled={saving || !text.trim() || !projectId}
            className="text-[13px] font-semibold rounded-lg px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: PAPER.navy, color: PAPER.accent }}
            data-testid="diary-todo-overview-submit-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add to-do
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodoOverview;
